import './style.scss';

import { Divider, Row, Space, Tag, Upload } from 'antd';
import { Button } from 'components';
import { Button as CustomButton, CustomTable } from 'components';
import Spinner from 'components/Spinner';
import Toast from 'components/Toast';
import PageContainer from 'containers/PageContainer';
import { IMAGES } from 'enum';
import { INTERNAL_LINKS } from 'enum';
import useDebouncedState from 'hooks/useDebouncedState';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useSelector } from 'react-redux';

import { handleApiErrors, performApiCallIfCompanySubIsActive } from '../../api/axiosInstance';
import { USER_API } from '../../api/user';
import { ConfirmModal } from '../../components';
import Text from '../../components/Text';
import withAuthentication from '../../hocs/withAuthentication';
import { checkIfStringContainsValue, formatPageTitle } from '../../utils/common';
import { selectStoreCompanyGroupByID } from '../../utils/storeSelectors';

const CSV_TEMPLATE = t => [
  {
    label: t('firstName'),
    fieldName: 'firstName',
  },
  {
    label: t('lastName'),
    fieldName: 'lastName',
  },
  {
    label: t('email'),
    fieldName: 'email',
  },
  {
    label: t('employeeNumber'),
    fieldName: 'employeeNumber',
  },
  {
    label: t('Group'),
    fieldName: 'group',
  },
  {
    label: t('role'),
    fieldName: 'role',
  },
  {
    label: t('sendInviteEmailFutureDate_optional'),
    fieldName: 'sendInviteEmailOnFutureDateOptional',
  },
  {
    label: t('sendInviteOnFutureTime_optional'),
    fieldName: 'sendInviteOnFutureTimeOptional',
  },
  { label: t('costCenter_optional'), fieldName: 'costCenterOptional' },
  { label: t('vehicleMake_optional'), fieldName: 'vehicleMakeOptional' },
  { label: t('vehicleModel_optional'), fieldName: 'vehicleModelOptional' },
  { label: t('vehicleYear_optional'), fieldName: 'vehicleYearOptional' },
  { label: t('homeAddressLine1_optional'), fieldName: 'homeAddressLine1Optional' },
  { label: t('homeAddressLine2_optional'), fieldName: 'homeAddressLine2Optional' },
  { label: t('homeAddressCity_optional'), fieldName: 'homeAddressCityOptional' },
  { label: t('homeAddressState_optional'), fieldName: 'homeAddressStateOptional' },
  { label: t('homeAddressZipCode_optional'), fieldName: 'homeAddressZipCodeOptional' },
  { label: t('homeAddressCountry_optional'), fieldName: 'homeAddressCountryOptional' },

  { label: t('mileageCapValue'), fieldName: 'mileageCapValue' },
  { label: t('mileageCapUnit'), fieldName: 'mileageCapUnit' },
  { label: t('complianceInsuranceStatus'), fieldName: 'complianceInsuranceStatus' },
  { label: t('complianceVehicleStatus'), fieldName: 'complianceVehicleStatus' },
];

const renderTag = (t, columnVal, row, columnName, minWidth) => {
  return (
    <>
      <span>{columnVal}</span>
      {row.errors ? (
        <div style={{ minWidth }}>
          {row.errors.map((tag, key) => {
            return (
              tag.param === columnName && (
                <Tag
                  color="volcano"
                  key={key}
                  style={{ whiteSpace: 'normal', textTransform: 'capitalize' }}
                >
                  {tag.msg === 'Invalid role.' ? t('roleIsUserManagerAdmin') : tag.msg}
                </Tag>
              )
            );
          })}
        </div>
      ) : null}
    </>
  );
};

const Columns = t => [
  {
    title: t('errors'),
    key: 'errors',
    dataIndex: 'errors',
  },
  {
    title: t('firstName'),
    dataIndex: 'firstName',
    key: 'firstName',
    sorter: {
      compare: (a, b) => a.firstName.localeCompare(b.firstName),
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'firstName'),
  },
  {
    title: t('lastName'),
    dataIndex: 'lastName',
    key: 'lastName',
    sorter: {
      compare: (a, b) => a.lastName.localeCompare(b.lastName),
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'lastName'),
  },
  {
    title: t('email'),
    dataIndex: 'email',
    key: 'email',
    sorter: {
      compare: (a, b) => a.email.localeCompare(b.email),
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'email'),
  },
  {
    title: t('employeeNumber'),
    dataIndex: 'employeeNumber',
    key: 'employeeNumber',
    sorter: {
      compare: (a, b) => {
        const numberA = a.employeeNumber ? `${a.employeeNumber}` : '';
        const numberB = b.employeeNumber ? `${b.employeeNumber}` : '';

        return numberA.localeCompare(numberB);
      },
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'employeeNumber'),
  },
  {
    title: t('Group'),
    dataIndex: 'group',
    key: 'group',
    sorter: {
      compare: (a, b) => a.group.localeCompare(b.group),
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'group'),
  },
  {
    title: t('role'),
    dataIndex: 'role',
    key: 'role',
    sorter: {
      compare: (a, b) => a.role.localeCompare(b.role),
    },
    checked: true,
    render: (role, row) => renderTag(t, role, row, 'role'),
  },
  {
    title: t('sendActivationEmailDate'),
    key: 'sendActivationEmailDate',
    checked: true,
    render: ({ sendInviteEmailOnFutureDateOptional, sendInviteOnFutureTimeOptional }, row) => {
      if (!sendInviteEmailOnFutureDateOptional && !sendInviteOnFutureTimeOptional) {
        return 'N/A';
      }
      return (
        <div>
          <div>
            {renderTag(
              t,
              sendInviteEmailOnFutureDateOptional,
              row,
              'sendInviteEmailOnFutureDateOptional',
            )}
          </div>
          <div>
            {renderTag(t, sendInviteOnFutureTimeOptional, row, 'sendInviteOnFutureTimeOptional')}
          </div>
        </div>
      );
    },
  },
  {
    title: t('Cost Center'),
    key: 'costCenterOptional',
    dataIndex: 'costCenterOptional',
    render: (role, row) => renderTag(t, role, row, 'costCenterOptional'),
  },
  {
    title: t('vehicleMake'),
    key: 'vehicleMakeOptional',
    dataIndex: 'vehicleMakeOptional',
    render: (role, row) => renderTag(t, role, row, 'vehicleMakeOptional'),
  },
  {
    title: t('vehicleModel'),
    key: 'vehicleModelOptional',
    dataIndex: 'vehicleModelOptional',
    render: (role, row) => renderTag(t, role, row, 'vehicleModelOptional'),
  },
  {
    title: t('vehicleYear'),
    key: 'vehicleYearOptional',
    dataIndex: 'vehicleYearOptional',
    render: (role, row) => renderTag(t, role, row, 'vehicleYearOptional'),
  },
  {
    title: t('homeAddressLine1'),
    key: 'homeAddressLine1Optional',
    dataIndex: 'homeAddressLine1Optional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressLine1Optional', '300px'),
  },
  {
    title: t('homeAddressLine2'),
    key: 'homeAddressLine2Optional',
    dataIndex: 'homeAddressLine2Optional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressLine2Optional', '300px'),
  },
  {
    title: t('homeAddressCity'),
    key: 'homeAddressCityOptional',
    dataIndex: 'homeAddressCityOptional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressCityOptional', '300px'),
  },
  {
    title: t('homeAddressState'),
    key: 'homeAddressStateOptional',
    dataIndex: 'homeAddressStateOptional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressStateOptional', '300px'),
  },
  {
    title: t('homeAddressZipCode'),
    key: 'homeAddressZipCodeOptional',
    dataIndex: 'homeAddressZipCodeOptional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressZipCodeOptional', '300px'),
  },
  {
    title: t('homeAddressCountry'),
    key: 'homeAddressCountryOptional',
    dataIndex: 'homeAddressCountryOptional',
    render: (role, row) => renderTag(t, role, row, 'homeAddressCountryOptional', '300px'),
  },

  {
    title: t('mileageCapValue'),
    key: 'mileageCapValue',
    dataIndex: 'mileageCapValue',
    render: (value, row) => renderTag(t, value, row, 'mileageCapValue', '150px'),
  },
  {
    title: t('mileageCapUnit'),
    key: 'mileageCapUnit',
    dataIndex: 'mileageCapUnit',
    render: (unit, row) => renderTag(t, unit, row, 'mileageCapUnit'),
  },
  {
    title: t('complianceInsuranceStatus'),
    key: 'complianceInsuranceStatus',
    dataIndex: 'complianceInsuranceStatus',
    render: (status, row) => renderTag(t, status, row, 'complianceInsuranceStatus', '150px'),
  },
  {
    title: t('complianceVehicleStatus'),
    key: 'complianceVehicleStatus',
    dataIndex: 'complianceVehicleStatus',
    render: (status, row) => renderTag(t, status, row, 'complianceVehicleStatus', '150px'),
  },
];

const MAX_FILE_SIZE = 2000000;

const CSVInvite = props => {
  const { t } = props;
  const currentCompany = useSelector(state => state.common.currentCompany);
  const groupList = useSelector(state => state.group.groupList);

  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [invalidUsers, setInvalidUsers] = useState([]);
  const [validUsers, setValidUsers] = useState([]);
  const emptyData = { firstName: '', lastName: '', email: '', group: '', role: '' };
  const csvHeaders = CSV_TEMPLATE(t).map(config => {
    return {
      label: config.label,
      key: config.fieldName,
    };
  });

  const addFullGroupToUser = (usersDetails, invitedUsers) => {
    invitedUsers.forEach(user => {
      const userIndex = usersDetails.findIndex(userDetails => user.email === userDetails.email);
      if (usersDetails[userIndex]) {
        const group = selectStoreCompanyGroupByID(user.groupId);

        if (group) {
          usersDetails[userIndex].group = { ...group };

          if (usersDetails[userIndex].group && usersDetails[userIndex].group.groupManager._id) {
            usersDetails[userIndex].group.groupManager =
              usersDetails[userIndex].group.groupManager._id;
          }

          if (usersDetails[userIndex].group.company && usersDetails[userIndex].group.company._id) {
            usersDetails[userIndex].group.company = usersDetails[userIndex].group.company._id;
          }
        }
      }
    });
  };

  // Ignore invalid user and send invite
  const continueInvite = async () => {
    setIsModalVisible(false);
    setLoading(true);

    // adjusted roles to use /api/user/invite endpoint
    const invitedUsers = await validUsers.map(user => {
      user.role = user.role.toLowerCase();
      if (user.role === 'admin' || user.role === 'manager') {
        user.role = 'company-' + user.role;
      }
      const sendActivationEmailDate =
        user.sendInviteEmailOnFutureDateOptional && user.sendInviteOnFutureTimeOptional
          ? moment(
              `${user.sendInviteEmailOnFutureDateOptional} ${user.sendInviteOnFutureTimeOptional}`,
              'MM/DD/YYYY HH:mm',
            ).toISOString()
          : undefined;

      let homeAddress;
      if (
        !!user.homeAddressLine1Optional ||
        !!user.homeAddressLine1Optiona2 ||
        !!user.homeAddressCityOptional ||
        !!user.homeAddressStateOptional ||
        !!user.homeAddressZipCodeOptional ||
        !!user.homeAddressCountryOptional
      ) {
        homeAddress = {
          streetOne: user.homeAddressLine1Optional,
          streetTwo: user.homeAddressLine2Optional ? user.homeAddressLine2Optional : undefined,
          city: user.homeAddressCityOptional,
          state: user.homeAddressStateOptional,
          postalCode: user.homeAddressZipCodeOptional,
          country: user.homeAddressCountryOptional,
        };
      }

      let vehicle;
      if (!!user.vehicleMakeOptional && !!user.vehicleModelOptional && !!user.vehicleYearOptional) {
        vehicle = {
          make: user.vehicleMakeOptional,
          model: user.vehicleModelOptional,
          year: user.vehicleYearOptional,
        };
      }

      return {
        email: user.email,
        firstName: user.firstName,
        group: user.group,
        groupId: user.groupId,
        groupName: user.groupName,
        lastName: user.lastName,
        role: user.role,
        employeeId: user.employeeNumber,
        sendActivationEmailDate,
        department: user.costCenterOptional,
        mileageCapValue: user.mileageCapValue,
        mileageCapUnit: user.mileageCapUnit,
        complianceInsuranceStatus: user.complianceInsuranceStatus,
        complianceVehicleStatus: user.complianceVehicleStatus,
        homeAddress,
        vehicle,
      };
    });

    invitedUsers.map(user => {
      const group = groupList.find(grp => user.group === grp.name);
      user.groupId = group._id;
      user.groupName = group.name;
      return user;
    });

    try {
      const result = await USER_API.inviteUsers(invitedUsers, currentCompany._id);

      if (result.data.success) {
        addFullGroupToUser(result.data.usersDetails, invitedUsers);
        Toast({
          type: 'success',
          message: t('invitationSent'),
        });
        setLoading(false);
        props.history.push(INTERNAL_LINKS.USER_MANAGER);
      }
    } catch (error) {
      handleApiErrors(error.response);
    }
    setLoading(false);
  };

  const [debouncedSearchValid, setSearchValid] = useDebouncedState();
  const [debouncedSearchInvalid, setSearchInvalid] = useDebouncedState();

  const filteredValid = useMemo(() => {
    let array = validUsers;

    if (debouncedSearchValid) {
      array = array.filter(user => {
        return (
          user.firstName.toLowerCase().includes(debouncedSearchValid.toLowerCase()) ||
          user.lastName.toLowerCase().includes(debouncedSearchValid.toLowerCase()) ||
          user.email.toLowerCase().includes(debouncedSearchValid.toLowerCase()) ||
          checkIfStringContainsValue(user.employeeNumber, debouncedSearchValid)
        );
      });
    }

    return array;
  }, [debouncedSearchValid, validUsers]);

  const filteredInvalid = useMemo(() => {
    let array = invalidUsers;

    if (debouncedSearchInvalid) {
      array = array.filter(user => {
        return (
          user.firstName.toLowerCase().includes(debouncedSearchInvalid.toLowerCase()) ||
          user.lastName.toLowerCase().includes(debouncedSearchInvalid.toLowerCase()) ||
          user.email.toLowerCase().includes(debouncedSearchInvalid.toLowerCase()) ||
          checkIfStringContainsValue(user.employeeNumber, debouncedSearchInvalid)
        );
      });
    }

    return array;
  }, [debouncedSearchInvalid, invalidUsers]);

  const getInvalidTableTitle = (invalid, valid) => {
    if (invalid >= 1 && valid === 0) {
      return t('usersCouldNotBeCreated', { count: invalid });
    } else if (invalid >= 1 && valid >= 0) {
      return t('validInvalidUsersValidationError', {
        validUsers: valid,
        count: invalid + valid,
      });
    }
  };

  const csvProps = {
    fileList: [],
    beforeUpload: file => {
      if (file.type !== 'text/csv') {
        Toast({
          type: 'error',
          message: t('onlyCsvAllowed'),
        });

        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        Toast({
          type: 'error',
          message: t('csvTooLarge', { count: (MAX_FILE_SIZE / 1048576).toFixed(0) }),
        });
        return false;
      }
      return file.type === 'text/csv';
    },
    customRequest: async options => {
      const { file } = options;
      const url = `${process.env.REACT_APP_HOST_API}user/import-users`;
      setInvalidUsers([]);
      setValidUsers([]);
      setLoading(true);
      let csvData = new FormData();
      let filteredUsers = [];
      csvData.append('file', file);

      try {
        const result = await performApiCallIfCompanySubIsActive('post', url, csvData, {
          'Content-Type': 'multipart/form-data',
          Accept: 'text/csv',
        });

        if (result.data.message.validUsers) {
          await setValidUsers(result.data.message.validUsers);
        }
        if (result.data.message.invalidUsers) {
          filteredUsers = await result.data.message.invalidUsers.filter(
            res =>
              res.email !== '' || res.firstName !== '' || res.lastName !== '' || res.role !== '',
          );
          await setInvalidUsers(filteredUsers);
        }

        if (!result.data.message.validUsers.length) {
          setLoading(false);
          return Toast({
            type: 'error',
            message: t('importUserCsvError'),
            description: t('usersNotCreatedInvalidMissingCsvValues'),
          });
        }
      } catch (error) {
        handleApiErrors(error.response);
      }
      setLoading(false);
    },
  };
  return (
    <PageContainer title={t('bulkCreateUsers')} className="csv-invite">
      <Helmet>
        <title>{formatPageTitle('Bulk Create Users')}</title>
      </Helmet>

      <Row>
        {props.t('Step 1')}: {t('downloadThisCSVTemplateAndAddUsers')}:
        <CSVLink
          target="_self"
          filename="Bulk_Create_User_template.csv"
          headers={csvHeaders}
          data={[emptyData]}
        >
          &nbsp; Bulk_Create_User_template.csv
        </CSVLink>
      </Row>
      <Divider />
      <Row>
        {props.t('Step 2')}: {props.t('Upload your file')}. &nbsp;
        <Upload showUploadList={false} listType="file" accept=".csv" {...csvProps}>
          <Button
            className="with-icon upload-button"
            type="secondary"
            size="sm"
            key="upload-btn"
            icon={<img className="download-button" src={IMAGES.UPLOAD_BOX_ICON} alt="upload csv" />}
            text="uploadCSV"
          />
        </Upload>
      </Row>

      {invalidUsers.length > 0 && (
        <>
          <Row>
            <CustomTable
              className="error-table"
              dataSource={filteredInvalid}
              hiddenColumns={['errors']}
              columns={Columns(t)}
              customTitle={getInvalidTableTitle(invalidUsers.length, validUsers.length)}
              onSearchTermChange={setSearchInvalid}
              scroll={{ x: 1200 }}
            />
          </Row>
        </>
      )}

      {validUsers.length > 0 && (
        <>
          <Row>
            <CustomTable
              rowKey={record => record.email}
              className="valid-table"
              dataSource={filteredValid}
              hiddenColumns={['errors']}
              columns={Columns(t)}
              customTitle={t('validInvalidUsersCanBeInvited', {
                validUsers: validUsers.length,
                count: invalidUsers.length + validUsers.length,
              })}
              onSearchTermChange={setSearchValid}
              scroll={{ x: 1200 }}
            />
          </Row>

          <br />

          <Row>
            <Space direction="horizontal" size="small" wrap>
              <CustomButton
                block
                size="large"
                type="secondary"
                text="Cancel"
                onClick={() => {
                  setInvalidUsers([]);
                  setValidUsers([]);
                }}
              />
              <CustomButton
                block
                type="primary"
                size="large"
                onClick={() => setIsModalVisible(true)}
                text={`createUsers`}
              />
            </Space>
          </Row>
        </>
      )}

      <ConfirmModal
        className="address-modal"
        title={t('confirmCreateUsers')}
        visible={isModalVisible}
        okText="Yes"
        onCancel={() => setIsModalVisible(false)}
        onOk={() => continueInvite()}
        message={
          <div>
            <Text variant="p" size="sm" style={{ marginBottom: 5 }}>
              {t('validInvalidUsersWillBeCreated', {
                validUsers: validUsers.length,
                count: validUsers.length + invalidUsers.length,
              })}
            </Text>

            <Text variant="p" size="sm">
              {t('sureYouWantToContinue')}
            </Text>
          </div>
        }
      />

      {loading && (
        <div className="loading-container">
          <Spinner />
        </div>
      )}
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(CSVInvite));
